<template>
  <div class="page page-manage">
    <div v-if="isLoading">
      <LoadingState />
    </div>
    <div v-if="!isLoading">
      <ChooseChannelModal
        v-if="state.isChooseChannelModalShowing"
        :channels="channels"
        :currentChannel="selectedChannel"
        :onClose="onCloseChooseChannelModal"
        :onSubmit="onChooseChannel" />
      <ChooseAdsModal
        v-if="state.isChooseAdsModalShowing"
        :adsPreset="adsPreset"
        :currentSelectedAds="selectedAd"
        :onClose="onCloseChooseAdsModal"
        :onSubmit="onChooseAds" />
      <div class="container">
        <div class="section-advertising">
          <div class="row">
            <div class="col-md-8">
              <PreviewBytearkPlayer :options="playerOptions" v-if="canPublishAds"/>
              <div class="embed-responsive embed-responsive-16by9 error-container" v-if="!canPublishAds">
                <div class="error-help" v-html="getErrorMessage()" />
              </div>
            </div>
            <div class="col-md-4">
              <div class="controlbar controlbar-vertical">
                <form v-on:submit.prevent="onSubmit">
                  <div class="management-ads">
                    <div class="management-ads-item">
                      <div class="subtitle">Choose Channel</div>
                      <div class="title"
                        v-if="channels.length === 0">
                        No Channel created
                      </div>
                      <div class="title"
                        v-if="channels.length > 0"
                        @click="state.isChooseChannelModalShowing = true">
                        {{selectedChannel.name}}
                        <span class="float-right"><i class="fas fa-chevron-down"></i></span>
                      </div>
                    </div>
                    <div class="management-ads-item">
                      <div class="subtitle">Choose Ads</div>
                      <div class="title"
                        v-if="adsPreset.length === 0">
                        No Ads created
                      </div>
                      <div class="title"
                        v-if="adsPreset.length > 0"
                        @click="state.isChooseAdsModalShowing = true">
                        {{selectedAd.name}}
                        <span class="float-right"><i class="fas fa-chevron-down"></i></span>
                      </div>
                    </div>
                    <div class="management-ads-item">
                      <button class="btn btn-primary btn-lg btn-block" type="submit"
                        :disabled="state.isPublishing || !canPublishAds">
                        <span v-if="!state.isPublishing">Publish Now</span>
                        <span v-if="state.isPublishing">Publishing...</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint no-underscore-dangle: "off" */
import _ from 'lodash';
import { publishAds } from '@/modules/api/Publisher';
import { loadChannels } from '@/modules/api/Channel';
import { loadAdsPreset } from '@/modules/api/AdsPreset';
import LoadingState from '@/components/elements/LoadingState';
import DropdownButton from '@/components/elements/DropdownButton';
import ChooseChannelModal from '@/components/parts/channel/ChooseChannelModal';
import ChooseAdsModal from '@/components/parts/ads-preset/ChooseAdsModal';
import PreviewBytearkPlayer from '@/components/parts/byteark-player/PreviewBytearkPlayer';

export default {
  components: {
    LoadingState,
    ChooseChannelModal,
    ChooseAdsModal,
    PreviewBytearkPlayer,
    DropdownButton,
  },
  data() {
    return {
      state: {
        isPublishing: false,
        isChooseAdsModalShowing: false,
        isChooseChannelModalShowing: false,
      },
      selectedAd: {
        name: null,
      },
      selectedChannel: {},
      channels: [],
      adsPreset: [],
      publisher: null,
      playerOptions: {
        fluid: true,
        autoplay: 'any',
        autoplayadsmuted: true,
        sources: [{
          src: 'https://thaipbs-live.cdn.byteark.com/live/playlist.m3u8',
          type: 'application/x-mpegURL',
        }],
        plugins: {
          bytearkFirework: {
            debug: true,
            subscriberUrl: null,
            allowAdReplace: true,
          },
        },
      },
      isLoading: false,
    };
  },
  created() {
    this.load();
  },
  computed: {
    channelId() {
      return this.$route.query.channel;
    },
    canPublishAds() {
      return this.channels.length > 0 && this.adsPreset.length > 0;
    },
    hostname() {
      if (process.env.PROXY_SERVER_URL) {
        return process.env.PROXY_SERVER_URL;
      }
      return window.location.host;
    },
  },
  watch: {
    selectedChannel() {
      this.setPlayerOptions();
    },
  },
  methods: {
    isSelectedChannels(channelId, index) {
      if (!this.channelId) {
        return index === 0;
      }
      return channelId === this.channelId;
    },
    async load() {
      this.isLoading = true;
      try {
        const channel = await loadChannels();
        const preset = await loadAdsPreset();

        this.channels = channel.data;
        this.adsPreset = preset.data;
        this.selectedAd = _.head(this.adsPreset);
        this.selectedChannel = this.findChannel(this.channelId);
        this.setPlayerOptions();
        this.isLoading = false;
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },
    getErrorMessage() {
      if (this.channels.length === 0 && this.adsPreset.length === 0) {
        return 'Please create <strong>Channel</strong> and <strong>Ads</strong>';
      } else if (this.channels.length === 0 && this.adsPreset.length > 0) {
        return 'Please create <strong>Channel</strong>';
      } else if (this.channels.length > 0 && this.adsPreset.length === 0) {
        return 'Please create <strong>Ads</strong>';
      }
      return 'Sorry, Something wrong here. Please try agian.';
    },
    async onSubmit() {
      try {
        const payload = { channelId: this.selectedChannel._id, adsPresetId: this.selectedAd._id };
        await publishAds(payload);
        this.setAdsPublishingStatus();
      } catch (e) {
        console.log(e);
      }
    },
    setPlayerOptions() {
      const currentChannel = this.selectedChannel;
      if (currentChannel) {
        this.playerOptions = {
          ...this.playerOptions,
          sources: [{
            src: currentChannel.streamUrl,
            type: 'application/x-mpegURL',
          }],
          plugins: {
            ...this.playerOptions.plugins,
            bytearkFirework: {
              ...this.playerOptions.plugins.bytearkFirework,
              subscriberUrl: `wss://${currentChannel.service.hostname}/pubsub/channels/${currentChannel._id}/messages`,
            },
          },
        };
      }
    },
    findChannel(channelId) {
      if (channelId) {
        return _.find(this.channels, item => item._id === channelId);
      }

      if (this.channels.length > 0) {
        // pick first channel
        return _.head(this.channels);
      }

      return false;
    },
    onCloseChooseChannelModal() {
      this.state.isChooseChannelModalShowing = false;
    },
    onChooseChannel(item) {
      this.selectedChannel = _.cloneDeep(item);
      this.$router.replace({ query: { channel: this.selectedChannel._id } });
      this.state.isChooseChannelModalShowing = false;
    },
    onCloseChooseAdsModal() {
      this.state.isChooseAdsModalShowing = false;
    },
    onChooseAds(item) {
      this.selectedAd = _.cloneDeep(item);
      this.state.isChooseAdsModalShowing = false;
    },
    setAdsPublishingStatus() {
      this.state.isPublishing = true;
      const lockPublishButton = setInterval(() => {
        this.state.isPublishing = false;
        clearInterval(lockPublishButton);
      }, 5000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/themes/helper.scss';
@import '@/assets/scss/themes/variables.scss';

.controlbar {
  .header {
    color: $gray-200;
  }
}

.page-manage {
  position: relative;
}

.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;

  width: 100%;
  height: 100%;
  background-color: #333;
  pointer-events: none;

  .error-help {
    font-size: $font-size-lg;
    // font-weight: bold;
  }
}

.management-ads {
  display: flex;
  flex-direction: row;
  background: $white;
  @extend .shadow;

  .management-ads-item {
    flex: 1;
    padding: $spacer;
    border: 1px solid white;
    border-left: 1px solid $gray-200;
    cursor: pointer;

    .title {
      font-size: $font-size-lg;
      font-weight: bold;
      position: relative;
    }

    .subtitle {
      color: $gray-600;
      font-size: $font-size-sm;
    }
  }

  .management-ads-item:last-child {
    display: flex;
    background-color: $gray-100;

    .btn {
      font-weight: bold;
      text-transform: uppercase;
      transition: all 0.1s ease-out;
    }

    .btn:not([disabled]) {
      @extend .zoom;
    }

    .btn:disabled, .btn[disabled] {
      background-color: $gray-600;
      border-color: $gray-600;
      cursor: not-allowed;
    }
  }
}

.controlbar.controlbar-vertical {
  .management-ads {
    flex-direction: column;
    .management-ads-item {
      border-left: 0;
      border-bottom: 1px solid $gray-200;
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/pages/ads-control/ManageAdvertising.vue